/**
 * Operation Panel Component
 * Left panel with mode tabs, input controls, configuration, and execute button
 * Matches the old admin playground layout
 */

import { useState, useCallback, useEffect } from 'react';
import { Wand2, Edit3, Layers, Bot, Keyboard, X, Upload, Cpu, Star } from 'lucide-react';
import { ImageUpload, type UploadedImage } from './ImageUpload';
import { DynamicConfigSelector } from './DynamicConfigSelector';
import { SystemPromptPopover } from '@/components/common';
import {
  useGenerateImageV2,
  useEditImageV2,
  useFuseImagesV2,
  useLuckyCharmGenerate,
  usePromptAssist,
  usePromptAssistTemplate,
  useImageModels,
  useDynamicImageConfig,
  usePlaygroundAgents,
  useLuckyCharmThemes,
  useLuckyCharmImages,
  useLuckyCharmFonts,
} from '../hooks/usePlayground';
import { useOutputSchemasForAgent } from '@/features/system-settings/hooks/useOutputSchemas';
import type { LuckyCharmInputMode, EmotionTrend } from '@/types/playground';
import { useChatModelsQuery } from '@/lib/queries';
import type { OperationMode } from '@/pages/PlaygroundPage';
import emojiRegex from 'emoji-regex';
import { useI18n } from '@/i18n';
import { FontPicker } from '@/components/FontPicker';

/**
 * Strip emoji characters from text (fonts used in lucky charm don't support emojis)
 * Uses emoji-regex package for comprehensive emoji detection
 */
const stripEmojis = (text: string): string => {
  const regex = emojiRegex();
  return text.replace(regex, '').trim();
};

interface SelectedImage {
  id: string;
  image_url: string;
  source: 'history' | 'library' | 'upload';
  base64?: string;
}

interface OperationPanelProps {
  operationMode: OperationMode;
  onModeChange: (mode: OperationMode) => void;
  selectedImageForEdit: SelectedImage | null;
  onSelectImageForEdit: (image: SelectedImage | null) => void;
  selectedImagesForFuse: SelectedImage[];
  onSelectImagesForFuse: (images: SelectedImage[]) => void;
  onProcessingStart?: (task: { operation: OperationMode; prompt: string; model: string; startedAt: number; taskId?: string; estimatedTimeMs?: number }) => void;
  onProcessingEnd?: () => void;
}

type InputMode = 'agent' | 'raw' | 'manual';

const MODE_ICONS = {
  generate: Wand2,
  edit: Edit3,
  fuse: Layers,
  lucky_charm: Star,
};

const MODE_COLORS = {
  generate: 'bg-green-600 hover:bg-green-700',
  edit: 'bg-blue-600 hover:bg-blue-700',
  fuse: 'bg-purple-600 hover:bg-purple-700',
  lucky_charm: 'bg-amber-600 hover:bg-amber-700',
};

export function OperationPanel({
  operationMode,
  onModeChange,
  selectedImageForEdit,
  onSelectImageForEdit,
  selectedImagesForFuse,
  onSelectImagesForFuse,
  onProcessingStart,
  onProcessingEnd,
}: OperationPanelProps) {
  const { t } = useI18n();
  const [inputMode, setInputMode] = useState<InputMode>('manual');
  const [userPromptForAgent, setUserPromptForAgent] = useState('');
  const [promptText, setPromptText] = useState('');
  const [generatingPrompt, setGeneratingPrompt] = useState(false);
  // Agent mode state
  const [selectedAgentType, setSelectedAgentType] = useState<string>('');
  const [selectedSchemaKey, setSelectedSchemaId] = useState<string | null>(null);
  // Raw LLM mode state
  const [selectedRawModel, setSelectedRawModel] = useState<string>('gpt-4o-mini');
  const [rawTemperature, setRawTemperature] = useState<number>(0.7);
  // Store last response's prompts for transparency display
  const [lastSystemPrompt, setLastSystemPrompt] = useState<string | undefined>();
  const [lastUserPrompt, setLastUserPrompt] = useState<string | undefined>();

  // Lucky Charm mode state
  const [luckyCharmInputMode, setLuckyCharmInputMode] = useState<LuckyCharmInputMode>('agent');
  const [luckyCharmPhrase, setLuckyCharmPhrase] = useState('');
  const [luckyCharmAgentType, setLuckyCharmAgentType] = useState<string>('');
  const [luckyCharmExtraPrompt, setLuckyCharmExtraPrompt] = useState('');
  const [luckyCharmLLMModel, setLuckyCharmLLMModel] = useState<string>('');
  const [luckyCharmLLMTemperature, setLuckyCharmLLMTemperature] = useState<number>(0.7);
  const [luckyCharmChartId, setLuckyCharmChartId] = useState('');
  const [luckyCharmEmotionTrend, setLuckyCharmEmotionTrend] = useState<EmotionTrend>('positive');
  const [luckyCharmThemeId, setLuckyCharmThemeId] = useState<string>('');
  const [luckyCharmBackgroundId, setLuckyCharmBackgroundId] = useState('');
  const [luckyCharmFontId, setLuckyCharmFontId] = useState<number | undefined>();
  const [generatingLuckyCharmPhrase, setGeneratingLuckyCharmPhrase] = useState(false);

  // Fetch Lucky Charm assets (themes, images and fonts)
  const { data: luckyCharmThemes = [], isLoading: themesLoading } = useLuckyCharmThemes();
  const { data: luckyCharmImages = [], isLoading: imagesLoading } = useLuckyCharmImages(luckyCharmThemeId || undefined);
  const { data: luckyCharmFontsData } = useLuckyCharmFonts();
  const defaultFontId = luckyCharmFontsData?.defaultFontId;

  // Auto-select default font when fonts load
  useEffect(() => {
    if (defaultFontId != null && luckyCharmFontId === undefined) {
      setLuckyCharmFontId(defaultFontId);
    }
  }, [defaultFontId, luckyCharmFontId]);

  // Fetch agents list
  const { data: agents = [], isLoading: agentsLoading } = usePlaygroundAgents();

  // Fetch output schemas for selected agent from centralized API
  const { data: schemasData, isLoading: schemasLoading } = useOutputSchemasForAgent(
    selectedAgentType || undefined
  );
  const outputSchemas = schemasData?.schemas || [];

  // Reset schema selection when agent changes
  useEffect(() => {
    // Reset schema when agent changes
    setSelectedSchemaId(null);
  }, [selectedAgentType]);

  // Fetch chat models for Raw LLM mode (from LiteLLM server)
  const { data: chatModels = [], isLoading: chatModelsLoading } = useChatModelsQuery();

  // Fetch prompt assist template for transparency
  const { data: promptTemplate } = usePromptAssistTemplate();

  // Fetch available image models
  const { data: modelsData, isLoading: modelsLoading } = useImageModels();
  const models = modelsData?.models || [];

  // Auto-select first agent when agents load
  useEffect(() => {
    if (agents.length > 0 && !selectedAgentType) {
      console.log('[OperationPanel] Auto-selecting first agent:', agents[0].agent_type);
      setSelectedAgentType(agents[0].agent_type);
    }
  }, [agents, selectedAgentType]);

  // Auto-select first chat model when chat models load
  useEffect(() => {
    if (chatModels.length > 0 && !selectedRawModel) {
      console.log('[OperationPanel] Auto-selecting first chat model:', chatModels[0].id);
      setSelectedRawModel(chatModels[0].id);
    }
  }, [chatModels, selectedRawModel]);

  // Log current selected agent for debugging
  useEffect(() => {
    console.log('[OperationPanel] selectedAgentType changed to:', selectedAgentType);
  }, [selectedAgentType]);

  // Dynamic config management using API models
  const {
    config,
    currentModel,
    setModel,
    setSize,
    setAspectRatio,
    setQuality,
    setWebSearch,
    setBackground,
  } = useDynamicImageConfig(models, operationMode === 'lucky_charm' ? 'generate' : operationMode);

  // Mutations
  const generateMutation = useGenerateImageV2();
  const editMutation = useEditImageV2();
  const fuseMutation = useFuseImagesV2();
  const luckyCharmMutation = useLuckyCharmGenerate();
  const promptAssistMutation = usePromptAssist();

  const maxFuseImages = currentModel?.max_images_fuse || 4;

  // Handle uploaded images for edit mode
  const handleEditImageUpload = useCallback((images: UploadedImage[]) => {
    if (images.length > 0) {
      const img = images[0];
      onSelectImageForEdit({
        id: img.id,
        image_url: img.preview,
        source: 'upload',
        base64: img.base64,
      });
    }
  }, [onSelectImageForEdit]);

  // Handle uploaded images for fuse mode
  const handleFuseImageUpload = useCallback((images: UploadedImage[]) => {
    const newImages = images.map((img) => ({
      id: img.id,
      image_url: img.preview,
      source: 'upload' as const,
      base64: img.base64,
    }));
    onSelectImagesForFuse([...selectedImagesForFuse, ...newImages].slice(0, maxFuseImages));
  }, [selectedImagesForFuse, onSelectImagesForFuse, maxFuseImages]);

  // Generate prompt with Agent (uses Agent's personality)
  const handleGeneratePromptWithAgent = async () => {
    const agentToUse = selectedAgentType;

    console.log('[OperationPanel] Agent Mode - generating with:', agentToUse, 'schema:', selectedSchemaKey);

    if (!userPromptForAgent.trim()) {
      console.warn('[OperationPanel] User prompt is empty');
      return;
    }
    if (!agentToUse) {
      console.warn('[OperationPanel] No agent selected');
      alert('' + t('playground.labels.pleaseSelectAgent') + '');
      return;
    }

    setGeneratingPrompt(true);
    try {
      const response = await promptAssistMutation.mutateAsync({
        user_input: userPromptForAgent,
        language: 'zh',
        agentType: agentToUse,
        operationMode: operationMode as 'generate' | 'edit' | 'fuse',
        output_schema_key: selectedSchemaKey || undefined,
      });
      setPromptText(response.prompt);
      console.log('[OperationPanel] Agent Mode - prompt generated, mode:', response.mode, 'schema applied:', !!selectedSchemaKey);
    } catch (error) {
      console.error('[OperationPanel] Failed to generate prompt:', error);
      const errorMessage = error instanceof Error ? error.message : '' + t('playground.labels.generateFailed') + '';
      alert(`${t('playground.labels.generateFailed')}: ${errorMessage}`);
    } finally {
      setGeneratingPrompt(false);
    }
  };

  // Generate prompt with Raw LLM (direct API with custom system prompt)
  const handleGeneratePromptWithRawLLM = async () => {
    console.log('[OperationPanel] Raw Mode - generating with model:', selectedRawModel);

    if (!userPromptForAgent.trim()) {
      console.warn('[OperationPanel] User prompt is empty');
      return;
    }

    setGeneratingPrompt(true);
    try {
      const response = await promptAssistMutation.mutateAsync({
        user_input: userPromptForAgent,
        language: 'zh',
        model: selectedRawModel,
        temperature: rawTemperature,
        operationMode: operationMode as 'generate' | 'edit' | 'fuse',
      });
      setPromptText(response.prompt);
      // Save prompts for transparency display
      setLastSystemPrompt(response.system_prompt);
      setLastUserPrompt(response.user_prompt);
      console.log('[OperationPanel] Raw Mode - prompt generated, tokens:', response.tokens_used);
    } catch (error) {
      console.error('[OperationPanel] Failed to generate prompt:', error);
      const errorMessage = error instanceof Error ? error.message : '' + t('playground.labels.generateFailed') + '';
      alert(`${t('playground.labels.generateFailed')}: ${errorMessage}`);
    } finally {
      setGeneratingPrompt(false);
    }
  };

  // Generate Lucky Charm phrase with Agent
  const handleGenerateLuckyCharmPhraseWithAgent = async () => {
    if (!luckyCharmAgentType) {
      alert('' + t('playground.labels.pleaseSelectAgent') + '');
      return;
    }

    setGeneratingLuckyCharmPhrase(true);
    try {
      // Build prompt for agent
      let prompt = luckyCharmExtraPrompt || '请生成一句简短的幸运符运势文案。';
      if (luckyCharmEmotionTrend) {
        prompt += `\n情绪趋势: ${luckyCharmEmotionTrend}`;
      }

      // Use prompt assist API with agent mode and output schema
      const response = await promptAssistMutation.mutateAsync({
        user_input: prompt,
        language: 'zh',
        agentType: luckyCharmAgentType,
        operationMode: 'generate',
        output_schema_key: 'lucky_charm',  // Use lucky_charm output schema
      });

      // Extract phrase from response and strip emojis (fonts don't support them)
      const rawPhrase = response.prompt.trim().replace(/^["'"]|["'"]$/g, '');
      const phrase = stripEmojis(rawPhrase);
      setLuckyCharmPhrase(phrase);
      console.log('[OperationPanel] Lucky Charm Agent generated phrase:', phrase);
    } catch (error) {
      console.error('[OperationPanel] Failed to generate lucky charm phrase:', error);
      const errorMessage = error instanceof Error ? error.message : '' + t('playground.labels.generateFailed') + '';
      alert(`${t('playground.labels.generateFailed')}: ${errorMessage}`);
    } finally {
      setGeneratingLuckyCharmPhrase(false);
    }
  };

  // Generate Lucky Charm phrase with LLM
  const handleGenerateLuckyCharmPhraseWithLLM = async () => {
    if (!luckyCharmLLMModel) {
      alert('' + t('playground.labels.pleaseSelectLLMModel') + '');
      return;
    }
    if (!luckyCharmExtraPrompt?.trim()) {
      alert('' + t('playground.labels.pleaseEnterPrompt') + '');
      return;
    }

    setGeneratingLuckyCharmPhrase(true);
    try {
      // Build prompt
      let userInput = luckyCharmExtraPrompt;
      if (luckyCharmEmotionTrend) {
        userInput += `\n情绪趋势: ${luckyCharmEmotionTrend}`;
      }

      // Use prompt assist API with raw LLM mode and custom system prompt
      const response = await promptAssistMutation.mutateAsync({
        user_input: userInput,
        language: 'zh',
        model: luckyCharmLLMModel,
        temperature: luckyCharmLLMTemperature,
        system_prompt: `你是一个专业的运势文案生成器。请根据用户的要求生成简短、积极的幸运符文案。
文案要求：
- 简短精炼（10-30个字）
- 积极向上、鼓舞人心
- 适合印在幸运符图片上
- 只输出文案本身，不要添加任何解释
- 不要使用任何 emoji 表情符号`,
      });

      // Extract phrase from response and strip emojis (fonts don't support them)
      const rawPhrase = response.prompt.trim().replace(/^["'"]|["'"]$/g, '');
      const phrase = stripEmojis(rawPhrase);
      setLuckyCharmPhrase(phrase);
      console.log('[OperationPanel] Lucky Charm LLM generated phrase:', phrase);
    } catch (error) {
      console.error('[OperationPanel] Failed to generate lucky charm phrase:', error);
      const errorMessage = error instanceof Error ? error.message : '' + t('playground.labels.generateFailed') + '';
      alert(`${t('playground.labels.generateFailed')}: ${errorMessage}`);
    } finally {
      setGeneratingLuckyCharmPhrase(false);
    }
  };

  // Execute operation (async mode: returns task_id immediately, parent polls for status)
  const handleExecute = async () => {
    if (!canProceed()) return;

    const taskInfo = {
      operation: operationMode,
      prompt: operationMode === 'lucky_charm' ? luckyCharmPhrase : promptText,
      model: config.model,
      startedAt: Date.now(),
    };

    try {
      let result;
      if (operationMode === 'generate') {
        result = await generateMutation.mutateAsync({
          prompt: promptText,
          model: config.model,
          size: config.size,
          aspect_ratio: config.aspect_ratio,
          quality: config.quality || undefined,
          output_format: config.output_format,
          web_search: config.web_search,
          sync: false,
        });
      } else if (operationMode === 'edit' && selectedImageForEdit) {
        result = await editMutation.mutateAsync({
          prompt: promptText,
          model: config.model,
          size: config.size,
          aspect_ratio: config.aspect_ratio,
          quality: config.quality || undefined,
          output_format: config.output_format,
          source_image: selectedImageForEdit.base64 || selectedImageForEdit.image_url,
          sync: false,
        });
      } else if (operationMode === 'fuse') {
        // Collect base64 images, fallback to URLs if base64 not available
        const sourceImages = selectedImagesForFuse.map((img) => img.base64 || img.image_url);
        result = await fuseMutation.mutateAsync({
          prompt: promptText,
          model: config.model,
          size: config.size,
          aspect_ratio: config.aspect_ratio,
          quality: config.quality || undefined,
          output_format: config.output_format,
          source_images: sourceImages,
          sync: false,
        });
      } else if (operationMode === 'lucky_charm') {
        // Lucky charm keeps sync mode (fast, different flow)
        onProcessingStart?.(taskInfo);
        result = await luckyCharmMutation.mutateAsync({
          phrase: stripEmojis(luckyCharmPhrase),
          theme_id: luckyCharmThemeId || undefined,
          background_image_id: luckyCharmBackgroundId || undefined,
          font_id: luckyCharmFontId,
        });
        onProcessingEnd?.();

        if (result && result.status === 'failed') {
          throw new Error(result.error || t('playground.labels.taskFailed'));
        }
        return;
      }

      // Async mode: notify parent with task_id for polling
      if (result?.task_id) {
        onProcessingStart?.({
          ...taskInfo,
          taskId: result.task_id,
          estimatedTimeMs: result.estimated_time_ms,
        });
      }

      // Check if task already failed (shouldn't happen in async mode, but safety check)
      if (result && result.status === 'failed') {
        onProcessingEnd?.();
        throw new Error(result.error || t('playground.labels.taskFailed'));
      }
    } catch (error) {
      console.error('Operation failed:', error);
      onProcessingEnd?.();
      const errorMessage = error instanceof Error ? error.message : t('playground.labels.operationFailed');
      alert(`${t('playground.labels.operationFailed')}: ${errorMessage}`);
    }
  };

  // Check if can proceed with operation
  const canProceed = () => {
    if (operationMode === 'lucky_charm') {
      // Always need a phrase to generate lucky charm
      return !!luckyCharmPhrase.trim();
    }
    if (!promptText.trim()) return false;
    if (operationMode === 'edit' && !selectedImageForEdit) return false;
    if (operationMode === 'fuse' && selectedImagesForFuse.length < 2) return false;
    return true;
  };

  const isProcessing = generateMutation.isPending || editMutation.isPending || fuseMutation.isPending || luckyCharmMutation.isPending;
  const ModeIcon = MODE_ICONS[operationMode];
  const modeKey = operationMode === 'lucky_charm' ? 'luckyCharm' : operationMode;

  return (
    <div className="p-6">
      {/* Mode Tabs */}
      <div className="flex gap-2 mb-6 bg-gray-100 p-1 rounded-lg">
        {(['generate', 'edit', 'fuse', 'lucky_charm'] as OperationMode[]).map((mode) => {
          const Icon = MODE_ICONS[mode];
          const isActive = operationMode === mode;
          const activeColor = mode === 'generate' ? 'text-green-600'
            : mode === 'edit' ? 'text-blue-600'
            : mode === 'fuse' ? 'text-purple-600'
            : 'text-amber-600';
          const modeLabelKey = mode === 'lucky_charm' ? 'luckyCharm' : mode;
          return (
            <button
              key={mode}
              onClick={() => onModeChange(mode)}
              className={`flex-1 py-2 px-3 rounded-md text-sm font-medium transition-all flex items-center justify-center gap-2 ${
                isActive ? `bg-white shadow ${activeColor}` : 'text-gray-600 hover:text-gray-900'
              }`}
            >
              <Icon className="w-4 h-4" />
              <span>{t(`playground.modes.${modeLabelKey}`)}</span>
            </button>
          );
        })}
      </div>

      {/* Mode Description */}
      <div className="mb-6 p-3 bg-gray-50 rounded-lg border border-gray-200">
        <div className="flex items-center gap-2 text-sm">
          <ModeIcon className="w-4 h-4 text-gray-500" />
          <span className="font-medium">{t(`playground.modes.${modeKey}`)}</span>
        </div>
        <p className="text-xs text-gray-500 mt-1">{t(`playground.modeDescriptions.${modeKey}`)}</p>
      </div>

      {/* Image Selection for Edit Mode */}
      {operationMode === 'edit' && (
        <div className="mb-6">
          <label className="block text-sm font-medium text-gray-700 mb-2">
            {t('playground.labels.selectEditImage')} <span className="text-red-500">*</span>
          </label>
          {selectedImageForEdit ? (
            <div className="relative mb-3">
              <img
                src={selectedImageForEdit.image_url}
                alt="Selected for edit"
                className="w-full h-32 object-cover rounded-lg border-2 border-blue-500"
              />
              <button
                onClick={() => onSelectImageForEdit(null)}
                className="absolute top-2 right-2 bg-red-500 text-white rounded-full w-6 h-6 flex items-center justify-center hover:bg-red-600"
              >
                <X className="w-3 h-3" />
              </button>
              {selectedImageForEdit.source === 'upload' && (
                <span className="absolute top-2 left-2 px-2 py-0.5 rounded-full text-xs font-medium bg-orange-100 text-orange-800">
                  <Upload className="w-3 h-3 inline mr-1" />{t('playground.labels.upload')}
                </span>
              )}
            </div>
          ) : (
            <ImageUpload
              onUpload={handleEditImageUpload}
              maxImages={1}
              compact={false}
            />
          )}
          <p className="text-xs text-gray-500 mt-2">{t('playground.labels.uploadOrSelect')}</p>
        </div>
      )}

      {/* Image Selection for Fuse Mode */}
      {operationMode === 'fuse' && (
        <div className="mb-6">
          <label className="block text-sm font-medium text-gray-700 mb-2">
            {t('playground.labels.selectFuseImages')} (2-{maxFuseImages}) <span className="text-red-500">*</span>
          </label>
          {selectedImagesForFuse.length > 0 && (
            <div className="grid grid-cols-4 gap-2 mb-3">
              {selectedImagesForFuse.map((img, idx) => (
                <div key={img.id} className="relative">
                  <img
                    src={img.image_url}
                    alt={`Selected ${idx + 1}`}
                    className="w-full h-16 object-cover rounded-lg border-2 border-purple-500"
                  />
                  <button
                    onClick={() => {
                      onSelectImagesForFuse(selectedImagesForFuse.filter((i) => i.id !== img.id));
                    }}
                    className="absolute -top-1 -right-1 bg-red-500 text-white rounded-full w-5 h-5 flex items-center justify-center hover:bg-red-600"
                  >
                    <X className="w-3 h-3" />
                  </button>
                  <span className="absolute bottom-1 left-1 bg-purple-600 text-white text-xs px-1 rounded">
                    {idx + 1}
                  </span>
                  {img.source === 'upload' && (
                    <span className="absolute top-0 left-0 bg-orange-500 text-white text-xs px-1 rounded-br">
                      <Upload className="w-2 h-2" />
                    </span>
                  )}
                </div>
              ))}
            </div>
          )}
          {selectedImagesForFuse.length < maxFuseImages && (
            <ImageUpload
              onUpload={handleFuseImageUpload}
              maxImages={maxFuseImages - selectedImagesForFuse.length}
              multiple
              compact={false}
            />
          )}
          <p className="text-xs text-gray-500 mt-2">
            {t('playground.labels.selected')} {selectedImagesForFuse.length} / {maxFuseImages}. {t('playground.labels.uploadOrSelect')}
          </p>
        </div>
      )}

      {/* Lucky Charm Configuration */}
      {operationMode === 'lucky_charm' && (
        <div className="mb-6 p-4 border border-amber-200 rounded-lg bg-amber-50">
          <h3 className="text-sm font-medium text-amber-800 mb-4 flex items-center gap-2">
            <Star className="w-4 h-4" />
            {t('playground.luckyCharm.config')}
          </h3>

          {/* Input Mode Selection */}
          <div className="mb-4">
            <label className="block text-sm font-medium text-gray-700 mb-2">{t('playground.luckyCharm.inputMode')}</label>
            <div className="flex gap-2">
              <button
                onClick={() => setLuckyCharmInputMode('agent')}
                className={`flex-1 py-2 px-3 rounded-lg border-2 text-sm font-medium transition-all flex items-center justify-center gap-2 ${
                  luckyCharmInputMode === 'agent'
                    ? 'bg-amber-100 border-amber-500 text-amber-700'
                    : 'bg-white border-gray-300 text-gray-700 hover:bg-gray-50'
                }`}
              >
                <Bot className="w-4 h-4" />
                <span>{t('playground.labels.agent')}</span>
              </button>
              <button
                onClick={() => setLuckyCharmInputMode('llm')}
                className={`flex-1 py-2 px-3 rounded-lg border-2 text-sm font-medium transition-all flex items-center justify-center gap-2 ${
                  luckyCharmInputMode === 'llm'
                    ? 'bg-amber-100 border-amber-500 text-amber-700'
                    : 'bg-white border-gray-300 text-gray-700 hover:bg-gray-50'
                }`}
              >
                <Cpu className="w-4 h-4" />
                <span>{t('playground.labels.llm')}</span>
              </button>
              <button
                onClick={() => setLuckyCharmInputMode('manual')}
                className={`flex-1 py-2 px-3 rounded-lg border-2 text-sm font-medium transition-all flex items-center justify-center gap-2 ${
                  luckyCharmInputMode === 'manual'
                    ? 'bg-amber-100 border-amber-500 text-amber-700'
                    : 'bg-white border-gray-300 text-gray-700 hover:bg-gray-50'
                }`}
              >
                <Keyboard className="w-4 h-4" />
                <span>{t('playground.labels.manual')}</span>
              </button>
            </div>
          </div>

          {/* Manual Mode: Phrase Input */}
          {luckyCharmInputMode === 'manual' && (
            <div className="mb-4">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                {t('playground.labels.fortunePhrase')} <span className="text-red-500">*</span>
              </label>
              <textarea
                value={luckyCharmPhrase}
                onChange={(e) => setLuckyCharmPhrase(e.target.value)}
                rows={3}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-amber-500 focus:border-amber-500"
                placeholder={t('playground.labels.fortunePhrasePlaceholder')}
              />
            </div>
          )}

          {/* Agent Mode: Agent Selection + Extra Prompt + Generate Button */}
          {luckyCharmInputMode === 'agent' && (
            <div className="mb-4 space-y-3">
              {/* Agent Selection */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  {t('playground.labels.selectAgent')} <span className="text-red-500">*</span>
                  {agentsLoading && <span className="ml-2 text-gray-400 text-xs">{t('playground.labels.loading')}</span>}
                </label>
                <select
                  value={luckyCharmAgentType}
                  onChange={(e) => setLuckyCharmAgentType(e.target.value)}
                  disabled={agentsLoading || agents.length === 0}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-amber-500 focus:border-amber-500 disabled:bg-gray-100"
                >
                  <option value="">{t('playground.labels.selectAgentPlaceholder')}</option>
                  {agents.map((agent) => (
                    <option key={agent.agent_type} value={agent.agent_type}>
                      {agent.name} ({agent.agent_type})
                    </option>
                  ))}
                </select>
              </div>

              {/* Emotion Trend */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">{t('playground.labels.emotionTrend')}</label>
                <select
                  value={luckyCharmEmotionTrend}
                  onChange={(e) => setLuckyCharmEmotionTrend(e.target.value as EmotionTrend)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-amber-500 focus:border-amber-500"
                >
                  <option value="positive">{t('playground.labels.emotionPositive')}</option>
                  <option value="neutral">{t('playground.labels.emotionNeutral')}</option>
                  <option value="negative">{t('playground.labels.emotionNegative')}</option>
                  <option value="mixed">{t('playground.labels.emotionMixed')}</option>
                </select>
              </div>

              {/* Extra Prompt */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  {t('playground.labels.extraPrompt')} <span className="text-gray-400">({t('playground.labels.optional')})</span>
                </label>
                <textarea
                  value={luckyCharmExtraPrompt}
                  onChange={(e) => setLuckyCharmExtraPrompt(e.target.value)}
                  rows={2}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-amber-500 focus:border-amber-500"
                  placeholder={t('playground.labels.extraPromptAgentPlaceholder')}
                />
              </div>

              {/* Generate Button */}
              <button
                onClick={handleGenerateLuckyCharmPhraseWithAgent}
                disabled={generatingLuckyCharmPhrase || !luckyCharmAgentType}
                className="w-full px-4 py-2.5 bg-amber-600 text-white rounded-lg font-medium hover:bg-amber-700 disabled:opacity-50 transition-colors flex items-center justify-center gap-2"
              >
                <Wand2 className={`w-4 h-4 ${generatingLuckyCharmPhrase ? 'animate-spin' : ''}`} />
                <span>{generatingLuckyCharmPhrase ? t('playground.labels.generating') : t('playground.labels.generateText')}</span>
              </button>
            </div>
          )}

          {/* LLM Mode: Model Selection + Temperature + Extra Prompt + Generate Button */}
          {luckyCharmInputMode === 'llm' && (
            <div className="mb-4 space-y-3">
              {/* Model Selection */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  {t('playground.labels.selectLLMModel')} <span className="text-red-500">*</span>
                  {chatModelsLoading && <span className="ml-2 text-gray-400 text-xs">{t('playground.labels.loading')}</span>}
                </label>
                <select
                  value={luckyCharmLLMModel}
                  onChange={(e) => setLuckyCharmLLMModel(e.target.value)}
                  disabled={chatModelsLoading || chatModels.length === 0}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-amber-500 focus:border-amber-500 disabled:bg-gray-100"
                >
                  <option value="">{t('playground.labels.selectModelPlaceholder')}</option>
                  {chatModels.map((model) => (
                    <option key={model.id} value={model.id}>
                      {model.id}
                    </option>
                  ))}
                </select>
              </div>

              {/* Temperature */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  {t('playground.labels.temperature')}: {luckyCharmLLMTemperature.toFixed(1)}
                </label>
                <input
                  type="range"
                  min="0"
                  max="2"
                  step="0.1"
                  value={luckyCharmLLMTemperature}
                  onChange={(e) => setLuckyCharmLLMTemperature(parseFloat(e.target.value))}
                  className="w-full h-2 bg-gray-200 rounded-lg appearance-none cursor-pointer"
                />
                <div className="flex justify-between text-xs text-gray-400 mt-1">
                  <span>{t('playground.labels.precise')} (0)</span>
                  <span>{t('playground.labels.balanced')} (1)</span>
                  <span>{t('playground.labels.creative')} (2)</span>
                </div>
              </div>

              {/* Emotion Trend */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">{t('playground.labels.emotionTrend')}</label>
                <select
                  value={luckyCharmEmotionTrend}
                  onChange={(e) => setLuckyCharmEmotionTrend(e.target.value as EmotionTrend)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-amber-500 focus:border-amber-500"
                >
                  <option value="positive">{t('playground.labels.emotionPositive')}</option>
                  <option value="neutral">{t('playground.labels.emotionNeutral')}</option>
                  <option value="negative">{t('playground.labels.emotionNegative')}</option>
                  <option value="mixed">{t('playground.labels.emotionMixed')}</option>
                </select>
              </div>

              {/* Extra Prompt */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  {t('playground.labels.extraPrompt')} <span className="text-red-500">*</span>
                </label>
                <textarea
                  value={luckyCharmExtraPrompt}
                  onChange={(e) => setLuckyCharmExtraPrompt(e.target.value)}
                  rows={2}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-amber-500 focus:border-amber-500"
                  placeholder={t('playground.labels.extraPromptLLMPlaceholder')}
                />
              </div>

              {/* Generate Button */}
              <button
                onClick={handleGenerateLuckyCharmPhraseWithLLM}
                disabled={generatingLuckyCharmPhrase || !luckyCharmLLMModel || !luckyCharmExtraPrompt?.trim()}
                className="w-full px-4 py-2.5 bg-amber-600 text-white rounded-lg font-medium hover:bg-amber-700 disabled:opacity-50 transition-colors flex items-center justify-center gap-2"
              >
                <Wand2 className={`w-4 h-4 ${generatingLuckyCharmPhrase ? 'animate-spin' : ''}`} />
                <span>{generatingLuckyCharmPhrase ? t('playground.labels.generating') : t('playground.labels.generateText')}</span>
              </button>
            </div>
          )}

          {/* Final Phrase (editable, shown for Agent/LLM modes only) */}
          {luckyCharmInputMode !== 'manual' && (
            <div className="mb-4">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                {t('playground.labels.fortunePhrase')} <span className="text-red-500">*</span>
              </label>
              <textarea
                value={luckyCharmPhrase}
                onChange={(e) => setLuckyCharmPhrase(e.target.value)}
                rows={3}
                className="w-full px-3 py-2 border border-amber-300 rounded-lg text-sm focus:ring-amber-500 focus:border-amber-500 bg-white"
                placeholder={t('playground.labels.clickGenerateOrInput')}
              />
              <p className="text-xs text-gray-500 mt-1">{t('playground.labels.editableAfterGenerate')}</p>
            </div>
          )}

          {/* Common Options */}
          <div className="border-t border-amber-200 pt-4 mt-4">
            <h4 className="text-xs font-medium text-amber-700 mb-3">{t('playground.labels.customOptions')} ({t('playground.labels.optional')})</h4>
            <div className="space-y-3">
              {/* Chart ID (for context in agent/llm modes) */}
              {(luckyCharmInputMode === 'agent' || luckyCharmInputMode === 'llm') && (
                <div>
                  <label className="block text-xs font-medium text-gray-600 mb-1">
                    {t('playground.labels.chartId')}
                  </label>
                  <input
                    type="text"
                    value={luckyCharmChartId}
                    onChange={(e) => setLuckyCharmChartId(e.target.value)}
                    className="w-full px-2 py-1.5 border border-gray-300 rounded text-xs focus:ring-amber-500 focus:border-amber-500"
                    placeholder={t('playground.labels.chartIdPlaceholder')}
                  />
                  <p className="text-xs text-gray-400 mt-0.5">
                    {t('playground.labels.chartIdHint')}
                  </p>
                </div>
              )}

              {/* Theme/Pack Selector */}
              <div>
                <label className="block text-xs font-medium text-gray-600 mb-1">
                  {t('playground.labels.backgroundPack')}
                  {themesLoading && <span className="ml-1 text-gray-400">({t('playground.labels.loading')})</span>}
                </label>
                <select
                  value={luckyCharmThemeId}
                  onChange={(e) => {
                    setLuckyCharmThemeId(e.target.value);
                    setLuckyCharmBackgroundId(''); // Reset background when theme changes
                  }}
                  disabled={themesLoading}
                  className="w-full px-2 py-1.5 border border-gray-300 rounded text-xs focus:ring-amber-500 focus:border-amber-500 disabled:bg-gray-100"
                >
                  <option value="">🎨 {t('playground.labels.allPacks')}</option>
                  {luckyCharmThemes.map((theme) => (
                    <option key={theme.id} value={theme.id} title={theme.description}>
                      {theme.display_name} ({theme.image_count})
                    </option>
                  ))}
                </select>
                {luckyCharmThemeId && luckyCharmThemes.find(t => t.id === luckyCharmThemeId)?.description && (
                  <p className="mt-0.5 text-xs text-gray-400">
                    {luckyCharmThemes.find(t => t.id === luckyCharmThemeId)?.description}
                  </p>
                )}
              </div>

              <div className="grid grid-cols-2 gap-3">
                {/* Background Image Selector */}
                <div>
                  <label className="block text-xs font-medium text-gray-600 mb-1">
                    {t('playground.labels.backgroundImage')}
                    {imagesLoading && <span className="ml-1 text-gray-400">({t('playground.labels.loading')})</span>}
                  </label>
                  <select
                    value={luckyCharmBackgroundId}
                    onChange={(e) => setLuckyCharmBackgroundId(e.target.value)}
                    disabled={imagesLoading}
                    className="w-full px-2 py-1.5 border border-gray-300 rounded text-xs focus:ring-amber-500 focus:border-amber-500 disabled:bg-gray-100"
                  >
                    <option value="">🎲 {t('playground.labels.random')}{luckyCharmThemeId ? ` (${t('playground.labels.currentPack')})` : ''}</option>
                    {luckyCharmImages.map((img) => (
                      <option key={img.id} value={img.id} title={img.filename}>
                        {img.display_name}
                      </option>
                    ))}
                  </select>
                </div>
                {/* Font Selector */}
                <div>
                  <label className="block text-xs font-medium text-gray-600 mb-1">
                    {t('playground.labels.font')}
                  </label>
                  <FontPicker
                    value={luckyCharmFontId ?? null}
                    onChange={(fontId) => setLuckyCharmFontId(fontId ?? undefined)}
                    fallbackLabel={t('playground.labels.defaultFont')}
                    compact
                    showManageLink={false}
                  />
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Prompt Input Section - Grouped (Hidden for Lucky Charm mode) */}
      {operationMode !== 'lucky_charm' && (
      <div className="mb-6 p-4 border border-gray-200 rounded-lg bg-white">
        {/* Input Mode Toggle */}
        <div className="mb-4">
          <label className="block text-sm font-medium text-gray-700 mb-2">{t('playground.labels.inputMode')}</label>
          <div className="flex gap-2">
            <button
              onClick={() => setInputMode('agent')}
              className={`flex-1 py-2 px-3 rounded-lg border-2 text-sm font-medium transition-all flex items-center justify-center gap-2 ${
                inputMode === 'agent'
                  ? 'bg-primary-100 border-primary-500 text-primary-700'
                  : 'bg-white border-gray-300 text-gray-700 hover:bg-gray-50'
              }`}
            >
              <Bot className="w-4 h-4" />
              <span>{t('playground.labels.agent')}</span>
            </button>
            <button
              onClick={() => setInputMode('raw')}
              className={`flex-1 py-2 px-3 rounded-lg border-2 text-sm font-medium transition-all flex items-center justify-center gap-2 ${
                inputMode === 'raw'
                  ? 'bg-primary-100 border-primary-500 text-primary-700'
                  : 'bg-white border-gray-300 text-gray-700 hover:bg-gray-50'
              }`}
            >
              <Cpu className="w-4 h-4" />
              <span>{t('playground.labels.llm')}</span>
            </button>
            <button
              onClick={() => setInputMode('manual')}
              className={`flex-1 py-2 px-3 rounded-lg border-2 text-sm font-medium transition-all flex items-center justify-center gap-2 ${
                inputMode === 'manual'
                  ? 'bg-primary-100 border-primary-500 text-primary-700'
                  : 'bg-white border-gray-300 text-gray-700 hover:bg-gray-50'
              }`}
            >
              <Keyboard className="w-4 h-4" />
              <span>{t('playground.labels.manual')}</span>
            </button>
          </div>
        </div>

        {/* Agent-Assisted Input */}
        {inputMode === 'agent' && (
          <div className="mb-4 space-y-4">
            {/* Agent Selection */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                {t('playground.labels.selectAgent')}
                {agentsLoading && <span className="ml-2 text-gray-400 text-xs">{t('playground.labels.loading')}</span>}
              </label>
              <select
                value={selectedAgentType}
                onChange={(e) => {
                  const newValue = e.target.value;
                  console.log('[OperationPanel] Agent selected:', newValue);
                  setSelectedAgentType(newValue);
                }}
                disabled={agentsLoading || agents.length === 0}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-primary-500 focus:border-primary-500 disabled:bg-gray-100 disabled:cursor-not-allowed"
              >
                {agents.length === 0 && !agentsLoading && (
                  <option value="">{t('playground.labels.noAgentsAvailable')}</option>
                )}
                {agents.map((agent) => (
                  <option key={agent.agent_type} value={agent.agent_type}>
                    {agent.name} ({agent.agent_type})
                  </option>
                ))}
              </select>
              {selectedAgentType && agents.find(a => a.agent_type === selectedAgentType)?.description && (
                <p className="mt-1 text-xs text-gray-500">
                  {agents.find(a => a.agent_type === selectedAgentType)?.description}
                </p>
              )}
            </div>

            {/* Output Schema Selection (only if agent has schemas) */}
            {selectedAgentType && (outputSchemas.length > 0 || schemasLoading) && (
              <div className="space-y-2">
                <label className="flex items-center gap-2 text-sm font-medium text-gray-700">
                  <span>{t('playground.labels.outputSchema')}</span>
                  {schemasLoading && <span className="text-gray-400 text-xs">{t('playground.labels.loading')}</span>}
                </label>
                <select
                  value={selectedSchemaKey || 'none'}
                  onChange={(e) => {
                    const value = e.target.value;
                    setSelectedSchemaId(value === 'none' ? null : value);
                  }}
                  disabled={generatingPrompt || schemasLoading || outputSchemas.length === 0}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm bg-white focus:ring-primary-500 focus:border-primary-500 disabled:bg-gray-100 disabled:cursor-not-allowed"
                >
                  <option value="none">{t('playground.labels.noSchemaConstraint')}</option>
                  {outputSchemas.map((schema) => (
                    <option key={schema.key} value={schema.key}>
                      {schema.name_zh || schema.name} ({schema.key})
                    </option>
                  ))}
                </select>
                {selectedSchemaKey && outputSchemas.find(s => s.key === selectedSchemaKey)?.description && (
                  <p className="text-xs text-gray-500">
                    {outputSchemas.find(s => s.key === selectedSchemaKey)?.description}
                  </p>
                )}
                {!selectedSchemaKey && outputSchemas.length > 0 && (
                  <p className="text-xs text-gray-400">
                    {t('playground.labels.schemaDescription')}
                  </p>
                )}
              </div>
            )}

            {/* User Guidance for Agent */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">{t('playground.labels.tellAgentWhatYouWant')}</label>
              <textarea
                value={userPromptForAgent}
                onChange={(e) => setUserPromptForAgent(e.target.value)}
                rows={3}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-primary-500 focus:border-primary-500"
                placeholder={t('playground.labels.userPromptPlaceholder')}
              />
            </div>

            {/* Generate Prompt Button */}
            <button
              onClick={handleGeneratePromptWithAgent}
              disabled={generatingPrompt || !userPromptForAgent?.trim() || !selectedAgentType}
              className="w-full px-4 py-2.5 bg-primary-600 text-white rounded-lg font-medium hover:bg-primary-700 disabled:opacity-50 transition-colors flex items-center justify-center gap-2"
            >
              <Wand2 className={`w-4 h-4 ${generatingPrompt ? 'animate-spin' : ''}`} />
              <span>{generatingPrompt ? t('playground.labels.generating') : t('playground.labels.generateDescription')}</span>
            </button>
          </div>
        )}

        {/* Raw LLM Mode Input */}
        {inputMode === 'raw' && (
          <div className="mb-4 space-y-4">
            {/* Model Selection */}
            <div>
              <label className="flex items-center gap-2 text-sm font-medium text-gray-700 mb-2">
                <span>{t('playground.labels.selectLLMModel')}</span>
                {chatModelsLoading && <span className="text-gray-400 text-xs">{t('playground.labels.loading')}</span>}
                {/* Show prompts used after generation */}
                {(lastSystemPrompt || lastUserPrompt) && (
                  <SystemPromptPopover
                    systemPrompt={lastSystemPrompt}
                    userPrompt={lastUserPrompt}
                    iconSize={14}
                  />
                )}
              </label>
              <select
                value={selectedRawModel}
                onChange={(e) => {
                  const newValue = e.target.value;
                  console.log('[OperationPanel] Raw model selected:', newValue);
                  setSelectedRawModel(newValue);
                }}
                disabled={chatModelsLoading || chatModels.length === 0}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-primary-500 focus:border-primary-500 disabled:bg-gray-100 disabled:cursor-not-allowed"
              >
                {chatModels.length === 0 && !chatModelsLoading && (
                  <option value="">{t('playground.labels.noModelsAvailable')}</option>
                )}
                {chatModels.map((model) => (
                  <option key={model.id} value={model.id}>
                    {model.id}
                  </option>
                ))}
              </select>
              <p className="mt-1 text-xs text-gray-500">
                {t('playground.labels.rawLLMHint')}
              </p>
            </div>

            {/* System Prompt Template (for transparency) */}
            {promptTemplate && (
              <div className="p-3 bg-gray-50 rounded-lg border border-gray-200">
                <div className="flex items-center justify-between mb-2">
                  <span className="text-xs font-medium text-gray-500">{t('playground.labels.systemPrompt')} ({operationMode})</span>
                </div>
                <pre className="text-xs text-gray-600 whitespace-pre-wrap max-h-24 overflow-y-auto">
                  {promptTemplate.system_prompts?.[operationMode]?.['zh'] || t('playground.labels.loading')}
                </pre>
              </div>
            )}

            {/* Temperature */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                {t('playground.labels.temperature')}: {rawTemperature.toFixed(1)}
              </label>
              <input
                type="range"
                min="0"
                max="2"
                step="0.1"
                value={rawTemperature}
                onChange={(e) => setRawTemperature(parseFloat(e.target.value))}
                className="w-full h-2 bg-gray-200 rounded-lg appearance-none cursor-pointer"
              />
              <div className="flex justify-between text-xs text-gray-400 mt-1">
                <span>{t('playground.labels.precise')} (0)</span>
                <span>{t('playground.labels.balanced')} (1)</span>
                <span>{t('playground.labels.creative')} (2)</span>
              </div>
            </div>

            {/* User Input for Raw Mode */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">{t('playground.labels.tellLLMWhatYouWant')}</label>
              <textarea
                value={userPromptForAgent}
                onChange={(e) => setUserPromptForAgent(e.target.value)}
                rows={3}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-primary-500 focus:border-primary-500"
                placeholder={t('playground.labels.userPromptPlaceholder')}
              />
            </div>

            {/* Generate Prompt Button */}
            <button
              onClick={handleGeneratePromptWithRawLLM}
              disabled={generatingPrompt || !userPromptForAgent?.trim() || !selectedRawModel}
              className="w-full px-4 py-2.5 bg-primary-600 text-white rounded-lg font-medium hover:bg-primary-700 disabled:opacity-50 transition-colors flex items-center justify-center gap-2"
            >
              <Wand2 className={`w-4 h-4 ${generatingPrompt ? 'animate-spin' : ''}`} />
              <span>{generatingPrompt ? t('playground.labels.generating') : t('playground.labels.generateDescription')}</span>
            </button>

            {/* Show actual User Prompt after generation */}
            {lastUserPrompt && (
              <div className="p-3 bg-blue-50 rounded-lg border border-blue-200">
                <div className="flex items-center justify-between mb-2">
                  <span className="text-xs font-medium text-blue-600">{t('playground.labels.userPromptSent')}</span>
                </div>
                <pre className="text-xs text-blue-800 whitespace-pre-wrap max-h-24 overflow-y-auto">
                  {lastUserPrompt}
                </pre>
              </div>
            )}
          </div>
        )}

        {/* Final Prompt */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            {operationMode === 'generate' ? t('playground.labels.imageDescription') : operationMode === 'edit' ? t('playground.labels.editInstruction') : t('playground.labels.fuseInstruction')}
            <span className="text-red-500"> *</span>
          </label>
          <textarea
            value={promptText}
            onChange={(e) => setPromptText(e.target.value)}
            rows={4}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-primary-500 focus:border-primary-500"
            placeholder={
              operationMode === 'generate'
                ? t('playground.labels.describeImageToGenerate')
                : operationMode === 'edit'
                ? t('playground.labels.describeEditInstruction')
                : t('playground.labels.describeFuseInstruction')
            }
          />
        </div>
      </div>
      )}

      {/* Configuration - Dynamic Model Selection (Hidden for Lucky Charm mode) */}
      {operationMode !== 'lucky_charm' && (
      <div className="mb-4">
        <DynamicConfigSelector
          models={models}
          config={config}
          currentModel={currentModel}
          operationMode={operationMode}
          onModelChange={setModel}
          onSizeChange={setSize}
          onAspectRatioChange={setAspectRatio}
          onQualityChange={setQuality}
          onWebSearchChange={setWebSearch}
          onBackgroundChange={setBackground}
          isLoading={modelsLoading}
        />
      </div>
      )}

      {/* Execute Button */}
      <button
        onClick={handleExecute}
        disabled={isProcessing || !canProceed()}
        className={`w-full px-4 py-3 rounded-lg font-medium transition-colors disabled:opacity-50 text-white ${MODE_COLORS[operationMode]}`}
      >
        {isProcessing ? (
          <>
            <span className="inline-block animate-spin mr-2">⏳</span>
            {t('playground.labels.processing')}
          </>
        ) : (
          <>
            <ModeIcon className="w-4 h-4 inline mr-2" />
            {t(`playground.modes.${modeKey}`)}
          </>
        )}
      </button>

      {/* Error Display */}
      {(generateMutation.isError || editMutation.isError || fuseMutation.isError || luckyCharmMutation.isError) && (
        <div className="mt-4 p-3 bg-red-50 border border-red-200 rounded-lg">
          <p className="text-sm text-red-600">
            {(generateMutation.error as Error)?.message ||
              (editMutation.error as Error)?.message ||
              (fuseMutation.error as Error)?.message ||
              (luckyCharmMutation.error as Error)?.message ||
              t('playground.labels.operationFailed')}
          </p>
        </div>
      )}
    </div>
  );
}
